import React, { useCallback, useEffect, useMemo } from 'react'

import { Box, Button, IconButton, Text, useBreakpointValue } from '@chakra-ui/react'

import ApplyV2Tooltip from '@core/components/ApplyDialogV2/ApplyV2Tooltip'
import GoogleMapPosition from '@core/components/GoogleMapPosition'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'

import type { LocationV2 } from '@page/post-v2/types'

import './PostPageV2GoogleMap.scss'

const baseClass = 'PostPageV2GoogleMap'

interface PostPageV2GoogleMapProps {
    location?: LocationV2 | null
    onBackToJob?: () => void
    onClose?: () => void
    showLocationNavigation?: boolean
    onPreviousLocation?: () => void
    onNextLocation?: () => void
}

function PostPageV2GoogleMap({
    location,
    onBackToJob,
    onClose,
    showLocationNavigation = false,
    onPreviousLocation,
    onNextLocation,
}: PostPageV2GoogleMapProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
    const handleClose = useCallback(() => onClose?.(), [onClose])

    const position = useMemo(
        () =>
            location
                ? {
                      lat: location.lat,
                      lng: location.lng,
                  }
                : null,
        [location]
    )

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleClose()
            }
        }

        window.addEventListener('keydown', onKeyDown)

        return () => {
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [handleClose])

    return (
        <Box className={baseClass} data-mobile={isMobile ? 'true' : 'false'}>
            <Box className={`${baseClass}__overlay`} onClick={handleClose} />

            <Box className={`${baseClass}__content`}>
                {isMobile ? (
                    <>
                        <Box className={`${baseClass}__mobile-map`}>
                            <button className={`${baseClass}__mobile-back`} onClick={handleClose}>
                                <span aria-hidden="true">←</span>
                            </button>
                            {position && (
                                <GoogleMapPosition
                                    position={position}
                                    zoom={8}
                                    height={'100%'}
                                    borderRadius={'0'}
                                    markerTitle={location?.formattedAddress}
                                    showZoomControls={!isMobile}
                                    showLocationNavigation={showLocationNavigation}
                                    onPreviousLocation={onPreviousLocation}
                                    onNextLocation={onNextLocation}
                                />
                            )}
                        </Box>
                        <Box className={`${baseClass}__mobile-footer`}>
                            <Text className={`${baseClass}__address`}>{location?.formattedAddress}</Text>
                            <Button className={`${baseClass}__back-button`} onClick={onBackToJob}>
                                {getTitle('13175', 'Back to job')}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Box className={`${baseClass}__desktop-card`}>
                        <Box className={`${baseClass}__desktop-header`}>
                            <Text className={`${baseClass}__title`}>{location?.formattedAddress}</Text>
                            <ApplyV2Tooltip label={getTitle('181', 'Close')}>
                                <IconButton
                                    aria-label="Close map"
                                    className={`${baseClass}__close`}
                                    onClick={handleClose}
                                    icon={
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            viewBox="0 0 16 16"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M13.6239 2.19526C13.8843 2.45561 13.8843 2.87772 13.6239 3.13807L8.8524 7.90963L13.8047 12.8619C14.0651 13.1223 14.0651 13.5444 13.8047 13.8047C13.5444 14.0651 13.1223 14.0651 12.8619 13.8047L7.90959 8.85243L3.13807 13.6239C2.87772 13.8843 2.45561 13.8843 2.19526 13.6239C1.93491 13.3636 1.93491 12.9415 2.19526 12.6811L6.96678 7.90963L2.37605 3.31886C2.1157 3.05851 2.11571 2.6364 2.37606 2.37605C2.63641 2.11571 3.05852 2.11571 3.31887 2.37606L7.90959 6.96682L12.6811 2.19526C12.9415 1.93491 13.3636 1.93491 13.6239 2.19526Z"
                                                fill="#77777C"
                                            />
                                        </svg>
                                    }
                                    width={'28px'}
                                    minWidth={'28px'}
                                    height={'28px'}
                                    borderRadius={'50%'}
                                    backgroundColor={'#F5F5F5'}
                                    _hover={{ backgroundColor: '#EAEAEB' }}
                                />
                            </ApplyV2Tooltip>
                        </Box>
                        <Box className={`${baseClass}__desktop-map`}>
                            {position && (
                                <GoogleMapPosition
                                    position={position}
                                    zoom={8}
                                    height={'100%'}
                                    borderRadius={'0'}
                                    markerTitle={location?.formattedAddress}
                                    showZoomControls
                                    showLocationNavigation={showLocationNavigation}
                                    onPreviousLocation={onPreviousLocation}
                                    onNextLocation={onNextLocation}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default PostPageV2GoogleMap
